import React, { useEffect, useState } from 'react';
import Form, { Actions, Conditional, EmailAddressValidator, Password, RequiredValidator, Select, TextField } from '../../../Components/Form';
import { useToasts } from '../../../Components/Toasts';
import API from '../../../Services/API';
import Loading from '../../../Components/Loading';
import Recaptcha from '../../../Services/Recaptcha';
import trackActivity from '../../../Components/Analytics/trackActivity';
import { PasswordLengthValidator, isAlreadyRegistered, registerFailureReason } from '../registration';

const loadCountries = async () => await import('../../../Data/countries.js');

const trackRegistration = (action, data = {}) => trackActivity('Registration', action, data);

const RegisterChallenge = ({ email, onSuccess, onAlreadyRegistered }) => {
    const [isGB, setIsGB] = useState(true);
    const [countries, setCountries] = useState(null);
    const [registering, setRegistering] = useState(false);
    const { showSuccess, showError } = useToasts();

    useEffect(() => {
        Recaptcha.init();
        loadCountries().then(module => {
            setCountries(module.default);
        });
    }, []);

    const register = async (data) => {
        trackRegistration('registration_submitted');

        let captchaToken;
        try {
            captchaToken = await Recaptcha.getToken('register');
        } catch (err) {
            trackRegistration('registration_recaptcha_failed');
            showError(API.parseErrorResponse(err));
            return;
        }

        data.captchaToken = captchaToken;

        return API.post('/users/register', data)
            .then((res) => {
                trackRegistration('registration_register_succeeded');
                waitForRegistrationComplete(res.userId, data.email);
            })
            .catch(err => {
                trackRegistration('registration_register_failed', {reason: registerFailureReason(err)});

                if (isAlreadyRegistered(err)) {
                    onAlreadyRegistered(data.email);
                    return;
                }

                showError(API.parseErrorResponse(err));
            });
    };

    const waitForRegistrationComplete = (userId, email) => {
        setRegistering(true);

        API.get('/users/waitForRegistration?userId=' + userId).then(() => {
            trackRegistration('registration_success');
            onSuccess?.(email);
        }).catch(err => {
            trackRegistration('registration_failed');
            showError(API.parseErrorResponse(err));
            setRegistering(false);
        });
    };

    const submit = async (data) => {
        if (isGB) {
            return register(data);
        }

        trackRegistration('registration_interest_submitted');

        return await API.post('/interests', data)
            .then(() => {
                trackRegistration('registration_interest_success');
                showSuccess('Thank you!', 'We\'ll let you know when we\'re available in your area.');
            })
            .catch(err => {
                trackRegistration('registration_interest_failed');
                showError(API.parseErrorResponse(err));
            });
    };

    if (registering) {
        return (
            <div className="my-4">
                <Loading size="2x" center />
                <p className="text-center text-muted text-sm mt-4">
                    <strong>Welcome!</strong><br />
                    Creating your account
                </p>
            </div>
        );
    }

    return <>
        <Form data={{ email, country: 'GB' }} onSubmit={submit}>
            {countries && <Select label="Country"
                name="country"
                options={Object.keys(countries).map(name => ({ label: name, value: countries[name] }))}
                onChange={v => setIsGB(v === 'GB')}
                validators={[RequiredValidator]}
            />}
            {!countries && <Select label="Country"
                name="country"
                options={[{ label: 'United Kingdom', value: 'GB' }]}
                disabled
            />}

            <Conditional condition={(data) => data.country !== 'GB'}>
                <div className="prose prose-sm surface-2 p-2 rounded-lg">
                    <p>Sorry, we're only operating in the UK at the moment.</p>

                    <p>We plan to expand into other areas soon.</p>

                    <p>Would you like to be notified when we're available in your area?</p>
                </div>
                <TextField
                    name="name"
                    label="Name"
                    validators={isGB ? [] : [RequiredValidator]}
                />
            </Conditional>

            <TextField type="email"
                name="email"
                label="Email"
                validators={[RequiredValidator, EmailAddressValidator]}
            />
            <Conditional condition={(data) => data.country === 'GB'}>
                <Password label="Password"
                    name="password"
                    validators={isGB ? [RequiredValidator, PasswordLengthValidator] : []}
                    showPrompts
                />
            </Conditional>
            <Actions submit={{label: isGB ? 'Register' : 'Keep me updated'}} />
        </Form>
    </>;
};

export default RegisterChallenge;
